import { createClient } from '@supabase/supabase-js'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? ''
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? ''

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY)

/** True if Supabase env vars are present. This does NOT mean the connection
 * or the `cms_data` table actually works — use pingSupabase() for that. */
export const isSupabaseConfigured = () =>
  !!(SUPABASE_URL && SUPABASE_ANON_KEY && SUPABASE_URL.startsWith('https://'))

export type SupabaseStatus =
  | { ok: true }
  | { ok: false; reason: 'not-configured' | 'table-missing' | 'error'; message: string }

/**
 * Actually tests the connection (not just whether env vars are set).
 * Catches the #1 cause of "my CMS edits don't show up on the live site":
 * the `cms_data` table was never created (supabase-schema.sql was never run),
 * so every save silently fails and only ever lands in this browser's localStorage.
 */
export async function pingSupabase(): Promise<SupabaseStatus> {
  if (!isSupabaseConfigured()) {
    return { ok: false, reason: 'not-configured', message: 'Supabase URL/key not set in .env — running in local-only mode.' }
  }
  try {
    const { error } = await supabase.from('cms_data').select('id').eq('id', 'main').limit(1)
    if (error) {
      // Postgres "relation does not exist" => table was never created
      if (error.code === '42P01' || /relation .* does not exist/i.test(error.message)) {
        return { ok: false, reason: 'table-missing', message: 'The cms_data table does not exist yet. Run supabase-schema.sql in your Supabase project\'s SQL editor.' }
      }
      return { ok: false, reason: 'error', message: error.message }
    }
    return { ok: true }
  } catch (e) {
    return { ok: false, reason: 'error', message: networkErrorHint(e) }
  }
}

/** "Failed to fetch" is the browser's generic network-error message — the request
 * never even completed. Give people a concrete list of causes instead of a
 * cryptic TypeError, since this is the #1 support question for this setup. */
function networkErrorHint(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e)
  if (/failed to fetch/i.test(raw)) {
    return 'Failed to fetch — the request to Supabase never went through. Most likely: (1) your Supabase project is paused (free-tier projects auto-pause after inactivity — check the Supabase dashboard), (2) VITE_SUPABASE_URL is wrong/mistyped, or (3) an ad-blocker/browser extension is blocking requests to supabase.co. Check the browser Network tab for the exact failing request.'
  }
  return raw
}

export async function loadDBFromSupabase(): Promise<Record<string, unknown> | null> {
  if (!isSupabaseConfigured()) return null
  try {
    const { data, error } = await supabase
      .from('cms_data')
      .select('db')
      .eq('id', 'main')
      .single()
    if (error) return null
    return (data?.db as Record<string, unknown>) ?? null
  } catch {
    return null
  }
}

/** Returns true if the save actually reached Supabase. Callers should surface
 * a visible warning to the user when this is false — a failed save that nobody
 * sees is exactly how "my CMS changes aren't showing up" bugs happen. */
export async function saveDBToSupabase(db: unknown): Promise<{ ok: boolean; message?: string }> {
  if (!isSupabaseConfigured()) return { ok: false, message: 'Supabase not configured — saved locally only.' }
  try {
    const { error } = await supabase.from('cms_data').upsert({
      id: 'main',
      db,
      updated_at: new Date().toISOString(),
    })
    if (error) {
      if (error.code === '42P01' || /relation .* does not exist/i.test(error.message)) {
        return { ok: false, message: 'cms_data table does not exist — run supabase-schema.sql in Supabase, then save again.' }
      }
      return { ok: false, message: error.message }
    }
    return { ok: true }
  } catch (e) {
    return { ok: false, message: networkErrorHint(e) }
  }
}
